import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { journeyAPI } from '../services/api';

const Settings = () => {
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('unknown');
  const [systemStatus, setSystemStatus] = useState(null);
  const [notificationTest, setNotificationTest] = useState({ loading: false, result: null });

  useEffect(() => {
    loadSystemStatus();
    testConnection();
  }, []);

  const loadSystemStatus = async () => {
    try {
      const response = await journeyAPI.getStatus();
      setSystemStatus(response.data.status);
    } catch (error) {
      console.error('Failed to load system status:', error);
    }
  };

  const testConnection = async () => {
    setLoading(true);
    try {
      await journeyAPI.healthCheck();
      setConnectionStatus('connected');
    } catch (error) {
      console.error('Health check failed:', error);
      setConnectionStatus('disconnected');
    } finally {
      setLoading(false);
    }
  };

  const testNotificationApi = async () => {
    setNotificationTest({ loading: true, result: null });
    
    try {
      const response = await journeyAPI.testNotificationApi();
      
      setNotificationTest({
        loading: false,
        result: {
          success: response.data.success,
          message: response.data.message,
          error: response.data.error
        }
      });
      
      if (response.data.success) {
        toast.success('Notification_API connection successful!');
      } else {
        toast.error(`Notification_API test failed: ${response.data.error || 'Unknown error'}`);
      }
      
    } catch (error) {
      setNotificationTest({
        loading: false,
        result: {
          success: false,
          message: 'Connection test failed',
          error: error.message
        }
      });
      toast.error(`Notification_API test failed: ${error.message}`);
    }
  };

  const triggerManualCheck = async () => {
    setLoading(true);
    
    try {
      const response = await journeyAPI.checkNotifications();
      const result = response.data.result;
      
      toast.success(
        `Manual check completed: ${result.sent} alerts sent, ${result.errors} errors`,
        { duration: 5000 }
      );
      
      // Refresh system status
      await loadSystemStatus();
      
    } catch (error) {
      toast.error(`Manual check failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshAll = async () => {
    setLoading(true);
    await Promise.all([
      testConnection(),
      loadSystemStatus()
    ]);
    setLoading(false);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Settings & System Status</h1>
        <p className="mt-2 text-gray-600">
          Monitor system health and test Notification_API connectivity
        </p>
      </div>

      {/* System Status */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">System Status</h2>
          <button
            onClick={refreshAll}
            disabled={loading}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            {loading ? '🔄 Refreshing...' : '🔄 Refresh'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Backend Connection */}
          <div className="text-center p-4 border rounded-lg">
            <div className={`w-4 h-4 mx-auto rounded-full mb-2 ${
              connectionStatus === 'connected' ? 'bg-green-500' :
              connectionStatus === 'disconnected' ? 'bg-red-500' : 'bg-yellow-500'
            }`}></div>
            <p className="font-medium text-gray-900">Backend API</p>
            <p className={`text-sm ${
              connectionStatus === 'connected' ? 'text-green-600' :
              connectionStatus === 'disconnected' ? 'text-red-600' : 'text-yellow-600'
            }`}>
              {connectionStatus === 'connected' ? 'Connected' :
               connectionStatus === 'disconnected' ? 'Disconnected' : 'Checking...'}
            </p>
          </div>

          {/* Database Status */}
          <div className="text-center p-4 border rounded-lg">
            <div className={`w-4 h-4 mx-auto rounded-full mb-2 ${
              systemStatus ? 'bg-green-500' : 'bg-gray-400'
            }`}></div>
            <p className="font-medium text-gray-900">Database</p>
            <p className="text-sm text-gray-600">
              {systemStatus ? `${systemStatus.total_active} active journeys` : 'Unknown'}
            </p>
          </div>

          {/* Monitoring Status */}
          <div className="text-center p-4 border rounded-lg">
            <div className="w-4 h-4 mx-auto rounded-full mb-2 bg-blue-500"></div>
            <p className="font-medium text-gray-900">Monitoring</p>
            <p className="text-sm text-gray-600">
              {systemStatus ? `${systemStatus.overdue} overdue` : 'Active'}
            </p>
          </div>
        </div>

        {/* Detailed Status */}
        {systemStatus && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Current Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="font-medium text-gray-700">Active Journeys</p>
                <p className="text-2xl font-bold text-green-600">{systemStatus.total_active}</p>
              </div>
              <div>
                <p className="font-medium text-gray-700">Upcoming</p>
                <p className="text-2xl font-bold text-blue-600">{systemStatus.upcoming}</p>
              </div>
              <div>
                <p className="font-medium text-gray-700">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{systemStatus.overdue}</p>
              </div>
              <div>
                <p className="font-medium text-gray-700">Notified</p>
                <p className="text-2xl font-bold text-yellow-600">{systemStatus.overdue_notified}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notification_API Testing */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Notification_API</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-900 mb-2">Connection Test</h3>
            <p className="text-sm text-gray-600 mb-3">
              Test that InForMent can reach the shared Notification_API service.
            </p>
            
            <button
              onClick={testNotificationApi}
              disabled={notificationTest.loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {notificationTest.loading ? '🧪 Testing...' : '🧪 Test Notification_API'}
            </button>
          </div>

          {/* Test Results */}
          {notificationTest.result && (
            <div className={`p-4 rounded-lg border ${
              notificationTest.result.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center mb-2">
                <span className="mr-2">
                  {notificationTest.result.success ? '✅' : '❌'}
                </span>
                <p className="font-medium">
                  {notificationTest.result.success ? 'Test Successful' : 'Test Failed'}
                </p>
              </div>
              <p className="text-sm">
                {notificationTest.result.message}
              </p>
              {notificationTest.result.error && (
                <p className="text-sm text-red-600 mt-1">
                  Error: {notificationTest.result.error}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Manual Controls */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Manual Controls</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-900 mb-2">Manual Notification Check</h3>
            <p className="text-sm text-gray-600 mb-3">
              Manually trigger the overdue journey check process. This will send notifications 
              for any overdue journeys that haven't been notified yet.
            </p>
            
            <button
              onClick={triggerManualCheck}
              disabled={loading}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {loading ? '⚡ Checking...' : '⚡ Trigger Manual Check'}
            </button>
          </div>
        </div>
      </div>

      {/* Configuration Info */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Configuration</h2>
        
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">🔧 Environment Setup</h3>
            <p className="text-sm text-blue-800 mb-2">
              InForMent sends overdue journey alerts through Notification_API.
            </p>
            <div className="bg-white p-3 rounded border text-sm font-mono text-gray-700">
              <div>Notification_API must be running and reachable from the InForMent backend.</div>
            </div>
          </div>

          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="font-medium text-green-900 mb-2">📋 PagerDuty Routing</h3>
            <ol className="text-sm text-green-800 space-y-1 list-decimal list-inside">
              <li>Open the Notification_API admin portal</li>
              <li>Set the InForMent app channel to pagerduty</li>
              <li>Use the Notification_API admin test to send a PagerDuty alert</li>
            </ol>
          </div>

          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="font-medium text-yellow-900 mb-2">⏰ Automatic Monitoring</h3>
            <p className="text-sm text-yellow-800">
              The system automatically checks for overdue journeys every 5 minutes. 
              When a journey becomes overdue, a PagerDuty alert will be triggered immediately.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
